/* Single-axis mentoring timeline (§6.11). One track runs from the mentoring
 * start to the latest end (the split/coprodução end, or the mentoring end when
 * there's no split). The mentoring end sits as an intermediate marker. Shared by
 * the list cards (compact) and the single-mentoring detail. */

export const DAY_MS = 24 * 60 * 60 * 1000;

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/**
 * Builds the timeline markers + the current ("now") position, all as percentages
 * along start → timelineEnd. Returns null when there's no started/ends window
 * yet (pending / awaiting_community).
 */
export function buildMentoringTimeline(timestamps = {}) {
  const start = dateMs(timestamps.started_at);
  const mentoringEnd = dateMs(timestamps.ends_at);
  const splitEndRaw = dateMs(timestamps.split_ends_at);

  if (!start || !mentoringEnd || mentoringEnd <= start) return null;

  // Coprodução (split) usually ends AFTER the mentoring, extending the track.
  // But it can also end on the SAME day — then both milestones collapse onto a
  // single marker/balloon instead of stacking two balloons at the same spot.
  const splitWithMentoring =
    !!splitEndRaw && sameDay(timestamps.split_ends_at, timestamps.ends_at);
  const hasSplit = !!splitEndRaw && splitEndRaw > mentoringEnd && !splitWithMentoring;

  const timelineEnd = hasSplit ? splitEndRaw : mentoringEnd;
  const span = timelineEnd - start;
  const posOf = (ms) => clampPct(((ms - start) / span) * 100);

  const markers = [
    { key: "start", kind: "start", label: "Início", short: "Início", iso: timestamps.started_at, pos: 0 },
    {
      key: "mentoring",
      kind: "mentoring",
      label: splitWithMentoring ? "Fim da mentoria e coprodução" : "Fim da mentoria",
      short: splitWithMentoring ? "Mentoria e coprodução" : "Mentoria",
      iso: timestamps.ends_at,
      pos: posOf(mentoringEnd),
    },
  ];

  if (hasSplit) {
    markers.push({
      key: "split",
      kind: "split",
      label: "Fim da coprodução",
      short: "Coprodução",
      iso: timestamps.split_ends_at,
      pos: 100,
    });
  }

  return { markers, nowPos: posOf(Date.now()), hasSplit };
}

function dateMs(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function clampPct(value) {
  return Math.min(100, Math.max(0, value));
}

// Same calendar day — compares the date portion only (TZ-safe, like the formatters).
function sameDay(isoA, isoB) {
  return !!isoA && !!isoB && isoA.slice(0, 10) === isoB.slice(0, 10);
}

// "10 de jan" — TZ-safe (parses the date portion); appends "de YYYY" off-year.
export function formatTimelineDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const month = MONTHS[(m || 1) - 1];
  const currentYear = new Date().getFullYear();
  return y === currentYear ? `${d} de ${month}` : `${d} de ${month} de ${y}`;
}

// "em 5 dias" / "há 2 dias" / "hoje" — most natural unit (dias < 14, semanas < 60, meses).
export function formatRelative(iso) {
  if (!iso) return "";
  const target = new Date(`${iso.slice(0, 10)}T00:00:00`).getTime();
  if (Number.isNaN(target)) return "";

  const diffDays = Math.round((target - Date.now()) / DAY_MS);
  if (diffDays === 0) return "hoje";

  const unit = unitLabel(Math.abs(diffDays));
  return diffDays > 0 ? `em ${unit}` : `há ${unit}`;
}

function unitLabel(days) {
  if (days < 14) return `${days} ${days === 1 ? "dia" : "dias"}`;
  if (days < 60) {
    const weeks = Math.round(days / 7);
    return `${weeks} ${weeks === 1 ? "semana" : "semanas"}`;
  }
  const months = Math.round(days / 30);
  return `${months} ${months === 1 ? "mês" : "meses"}`;
}
